import { useContext, useMemo } from "react";
import {
    Icon,
    IconChecklist,
    IconHexagons,
    IconHome,
    IconProgressCheck,
    IconQrcode,
    IconShieldCheck,
} from "@tabler/icons-react";
import { AbilityContext } from "src/authentication/context/Can";
import { useOffersMyRota } from "src/authentication/hooks/useOffersMyRota";
import { Action } from "src/casl/Action";
import { useMyChildProtectionInvolvement } from "src/child-protection/clearances/mine/hooks/useMyChildProtectionInvolvement";
import { MY_CHILD_PROTECTION_PATH } from "src/child-protection/clearances/mine/paths";
import { useMyCheckInPasses } from "src/calendar-events/check-in-passes/useMyCheckInPasses";
import { MY_CHECK_IN_PASSES_PATH } from "src/calendar-events/check-in-passes/mine/paths";

export interface MainMenuItem {
    icon: Icon;
    label: string;
    shortLabel?: string;

    to: string;

    bottomNav: boolean;

    end?: boolean;
    bottomEnd?: boolean;
}

/**
 * Constructs a maximum of 5 menu items for the main menu depending on the
 * user's roles.
 */
export const useMainMenuItems = () => {
    const ability = useContext(AbilityContext);
    const offersMyRota = useOffersMyRota();
    const { involved: involvedInChildProtection } = useMyChildProtectionInvolvement();
    const { passes } = useMyCheckInPasses();
    const hasPasses = !!passes?.length;

    return useMemo(() => {
        const menuItems: MainMenuItem[] = [
            // Always contains home
            {
                icon: IconHome,
                label: "Home",
                to: "/public",
                end: true,
                bottomEnd: true,
                bottomNav: true,
            },
        ];

        const isVolunteer = ability.can(Action.Read, "Ministry");

        // Forms & bookings (merged event bookings + consent/registration)
        // Only show on bottom nav if they aren't a volunteer
        menuItems.push({
            icon: IconProgressCheck,
            label: "Forms & bookings",
            shortLabel: "Forms",
            to: "/public/forms",
            bottomNav: !isVolunteer,
        });

        // A volunteer, or anybody else with a duty still to come (`SELF04`)
        if (offersMyRota) {
            menuItems.push({
                icon: IconChecklist,
                label: "My rota",
                to: "/my-rota",
                bottomNav: true,
            });
        }

        // Only for somebody child protection applies to, as on the home screen (`[CLR60B]`)
        if (involvedInChildProtection) {
            menuItems.push({
                icon: IconShieldCheck,
                label: "My child protection & training",
                to: MY_CHILD_PROTECTION_PATH,
                bottomNav: false,
            });
        }

        // Only while a pass can be used, as the shortcut on Home. A volunteer's bar is already full
        if (hasPasses) {
            menuItems.push({
                icon: IconQrcode,
                label: "Check-in passes",
                shortLabel: "Passes",
                to: MY_CHECK_IN_PASSES_PATH,
                bottomNav: !isVolunteer,
            });
        }

        if (isVolunteer) {
            menuItems.push({
                icon: IconHexagons,
                label: "Ministries",
                shortLabel: "Ministries",
                to: "/ministries",
                bottomNav: true,
                end: true,
            });
        }

        return menuItems;
    }, [ability, offersMyRota, involvedInChildProtection, hasPasses]);
};
